import { useEffect, useState } from "react";

import { Ban, Unlock } from "lucide-react";

import type { AdminUserRowSchema } from "@/api/generated/types.gen";
import { SpotlightSearch } from "@/components/CasesList/components/SpotlightSearch";
import { PageHeader } from "@/components/PageHeader";
import { UserIdentity } from "@/components/UserIdentity";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUpdateAdminUser } from "@/hooks/mutations/use-admin-users";
import { useAdminUsers } from "@/hooks/queries/use-admin-users";
import { useMe } from "@/hooks/queries/use-me";
import { cn } from "@/utils/ui";

/** Debounce a changing value by `delay` ms. */
const useDebounced = <T,>(value: T, delay = 300): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

/** The identity roles v1 access levels derive capabilities from. */
const USER_TYPES = ["internal", "expert", "referrer", "provider"] as const;

/** Friendly labels for the raw auth backends (design: Report Studio UI Update). */
const AUTH_TYPE_LABELS: Record<string, string> = {
  msal: "Microsoft SSO",
  email_otp: "Email code",
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const UserRow = ({ user }: { user: AdminUserRowSchema }) => {
  const update = useUpdateAdminUser();
  const isEmailCodeUser = user.auth_type === "email_otp";

  return (
    <TableRow className="hover:bg-[#E8FDFF]/50">
      <TableCell>
        <UserIdentity name={user.name} email={user.email} />
      </TableCell>
      <TableCell>
        <select
          aria-label={`User type: ${user.email}`}
          className="h-9 min-w-[120px] rounded-lg border border-border bg-transparent px-2 font-head text-[13px] outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          value={user.user_type ?? "internal"}
          disabled={update.isPending}
          onChange={(e) =>
            update.mutate({
              userId: user.id,
              body: { user_type: e.target.value as (typeof USER_TYPES)[number] },
            })
          }
        >
          {USER_TYPES.map((t) => (
            <option key={t} value={t}>
              {capitalize(t)}
            </option>
          ))}
        </select>
      </TableCell>
      <TableCell className="text-foreground-muted">
        {(user.auth_type && AUTH_TYPE_LABELS[user.auth_type]) ||
          user.auth_type ||
          "—"}
      </TableCell>
      <TableCell>
        <Switch
          aria-label={`Admin: ${user.email}`}
          checked={Boolean(user.is_admin)}
          // MDRS-581: admin requires Microsoft sign-in. The backend 400s a
          // grant to an email-code user, so don't offer the toggle at all —
          // but keep it live for an existing admin so the flag can be cleared.
          disabled={update.isPending || (isEmailCodeUser && !user.is_admin)}
          title={
            isEmailCodeUser && !user.is_admin
              ? "Admin requires Microsoft sign-in. This user signs in with an email code."
              : undefined
          }
          onCheckedChange={(next) => {
            if (
              next &&
              !window.confirm(
                `Grant admin to ${user.email}? Admins get full access to every case and all admin pages.`
              )
            ) {
              return;
            }
            update.mutate({ userId: user.id, body: { is_admin: next } });
          }}
        />
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            user.is_blocked
              ? "text-warning-800 hover:bg-warning-50 hover:text-warning-800"
              : "text-foreground-muted hover:bg-destructive-50 hover:text-destructive"
          )}
          onClick={() =>
            update.mutate({
              userId: user.id,
              body: { is_blocked: !user.is_blocked },
            })
          }
          disabled={update.isPending}
        >
          {user.is_blocked ? (
            <Unlock className="size-4" />
          ) : (
            <Ban className="size-4" />
          )}
          {user.is_blocked ? "Unblock" : "Block"}
        </Button>
      </TableCell>
    </TableRow>
  );
};

const UsersAdminPage = () => {
  const { data: me, isLoading: meLoading } = useMe();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);

  const isAdmin = Boolean(me?.user?.is_admin);
  const { data: users = [], isLoading } = useAdminUsers({
    q: debouncedSearch || undefined,
    enabled: isAdmin,
  });

  if (meLoading) {
    return <div className="px-4 py-4 text-foreground-muted">Loading…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="px-4 py-4 text-foreground-muted">
        You don’t have access to this page.
      </div>
    );
  }

  return (
    <div className="space-y-2 p-2 pl-0">
      <div className="rounded-2xl border border-foreground-muted/20 bg-background-white p-4">
        <PageHeader
          title="Users"
          description="All internal and external users. User type sets the access level; admins bypass everything."
        />
      </div>

      <div className="rounded-2xl border border-foreground-muted/20 bg-background-white">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <h2 className="text-base font-semibold tracking-[-0.01em]">All users</h2>
          <div className="w-[280px] shrink-0">
            <SpotlightSearch
              value={search}
              onChange={setSearch}
              placeholder="Search name or email"
            />
          </div>
        </div>

        <Table className="border-t border-foreground-muted/20">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>User type</TableHead>
              <TableHead>Sign-in method</TableHead>
              <TableHead>Admin</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5}>Loading…</TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>No users found.</TableCell>
              </TableRow>
            ) : (
              users.map((user) => <UserRow key={user.id} user={user} />)
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default UsersAdminPage;
